import React, { ChangeEvent, Fragment, ReactElement, useEffect, useState } from 'react';
import {
    Box,
    Backdrop,
    CircularProgress,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Button,
    Grid2,
    TablePagination,
    Paper,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { RootState } from '../../redux/reducers';
import { importProjects } from '../../redux/actions/projectActions';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { ClientDto, ProjectDto } from '../../api/ClientApi';
import AddIcon from '@mui/icons-material/Add';
import ProjectForm from './form/ProjectForm';
import { StyledTableCell } from '../../common/components/styled/StyledTable';
import { DetailsContainerBox, DetailsTableContainerBox } from '../../common/ResponsiveStyle';
import { canManipulateProjects, isEmployeeProjectManager } from '../../redux/selectors/employeeSelector';
import { getAllProjectsForMe } from '../../redux/selectors/projectsSelector';

type ProjectListTableInfo = {
    projectId?: number;
    projectName?: string;
    clientName?: string;
};

function createData(project: ProjectDto, client?: ClientDto): ProjectListTableInfo {
    return {
        projectId: project.id,
        clientName: project?.client?.shortName ?? project?.client?.name ?? client?.shortName ?? client?.name,
        projectName: project.name,
    };
}

export default function ProjectsList(): ReactElement {
    const clients = useAppSelector((state: RootState) => state.clients.clients);

    const myProjects = useAppSelector(getAllProjectsForMe);
    const projects = useAppSelector((state) => state.projects.projects);

    const canChangeProjects = useAppSelector(canManipulateProjects);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [openAddProjectForm, setOpenAddProjectForm] = useState<boolean>(false);
    const [filter, setFilter] = useState<string>('');

    const dispatch = useAppDispatch();

    const navigate = useNavigate();

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    useEffect(() => {
        if (!projects) {
            dispatch(importProjects());
        }
    }, [dispatch]);

    const handleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFilter(value);
    };

    const handleOpenProjectForm = () => {
        setOpenAddProjectForm(true);
    };

    const handleCloseProjectForm = () => {
        setOpenAddProjectForm(false);
    };

    const filteredProjects = myProjects?.filter((project) => {
        const client = clients?.find((c) => c.id === project?.clientId);

        return (
            project.name?.toLowerCase().includes(filter.toLowerCase()) ||
            client?.shortName?.toLowerCase().includes(filter.toLowerCase()) ||
            client?.name?.toLowerCase().includes(filter.toLowerCase())
        );
    });

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - (filteredProjects?.length ?? 0)) : 0;

    const visibleProjects = React.useMemo(
        () =>
            !filteredProjects ? [] : [...filteredProjects].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [page, rowsPerPage, clients, myProjects, filter],
    );

    const goToProject = (projectId?: number) => {
        if (!!projectId) {
            navigate(projectId.toString());
        } else {
            alert('Ne postoje detalji o skladištu');
        }
    };

    React.useEffect(() => {
        if (!filteredProjects || filteredProjects?.length <= page * rowsPerPage) {
            setPage(0);
        }
    }, [filteredProjects, page]);

    function Row(props: { row: ReturnType<typeof createData> }) {
        const { row } = props;

        return (
            <TableRow
                sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#eee' },
                }}
                onClick={() => goToProject(row.projectId)}
            >
                <TableCell component="th" scope="row">
                    {row.projectId ?? '/'}
                </TableCell>
                <TableCell align="center" component="th" scope="row">
                    {row.projectName ?? '/'}
                </TableCell>
                <TableCell align="center">{row.clientName ?? '/'}</TableCell>
            </TableRow>
        );
    }

    return myProjects === null ? (
        <Fragment>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={!Boolean(myProjects)}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Fragment>
    ) : (
        <>
            <DetailsContainerBox
                sx={{
                    p: 2,
                    backgroundColor: (theme) => (theme.palette.mode === 'light' ? 'white' : theme.palette.grey[900]),
                }}
            >
                <Grid2 container sx={{ py: 2, px: { xs: 2, sm: 4 }, justifyContent: 'space-between', gap: 2 }}>
                    <Grid2>
                        <Typography variant="h5">Projekti</Typography>
                    </Grid2>
                    {canChangeProjects && (
                        <Grid2>
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={handleOpenProjectForm}
                                startIcon={<AddIcon />}
                                sx={{ textTransform: 'none' }}
                            >
                                Novi projekat
                            </Button>
                        </Grid2>
                    )}
                </Grid2>
                <hr />
                <Box sx={{ p: { xs: 1, sm: 4 }, pt: 2 }}>
                    <TextField
                        size="small"
                        sx={{ mb: 1, width: '100%' }}
                        placeholder="Filter"
                        value={filter}
                        onChange={handleFilterChange}
                    />
                    <Box sx={{ p: 0, mt: 2 }}></Box>

                    <Box sx={{ width: '100%' }}>
                        <Paper sx={{ width: '100%', mb: 2 }}>
                            <DetailsTableContainerBox>
                                <Table size="small" aria-label="purchases">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>Id</StyledTableCell>
                                            <StyledTableCell align="center">Naziv</StyledTableCell>
                                            <StyledTableCell align="center">Klijent</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {visibleProjects.length === 0 && (
                                            <TableRow sx={{ height: 500 }}>
                                                <TableCell rowSpan={3} colSpan={3}>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 50,
                                                                width: 400,
                                                                margin: 'auto',
                                                                color: '#aaa',
                                                            }}
                                                        >
                                                            Nema projekata
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {visibleProjects?.map((project) => {
                                            const client = clients?.find((c) => c.id === project?.clientId);
                                            const newRowInfo: ProjectListTableInfo = createData(project, client);

                                            return <Row key={project.id} row={newRowInfo} />;
                                        })}
                                        {emptyRows > 0 && (
                                            <TableRow
                                                style={{
                                                    height: 40 * emptyRows,
                                                }}
                                            >
                                                <TableCell colSpan={6} />
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </DetailsTableContainerBox>
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50]}
                                component="div"
                                count={filteredProjects?.length ?? 0}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Paper>
                    </Box>
                </Box>
            </DetailsContainerBox>
            {canChangeProjects && <ProjectForm open={openAddProjectForm} closeForm={handleCloseProjectForm} />}
        </>
    );
}
